'use client'

import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import Map, { Marker, type MapRef } from 'react-map-gl/mapbox'
import useSupercluster from 'use-supercluster'
import { EVENTS } from '#app/lib/analytics/events'
import { track } from '#app/lib/analytics/track.client'
import { type ProfileData } from '#types/database.types'
import { ClusterMarker } from './cluster-marker'
import { ProfileClusterModal } from './profile-cluster-modal'
import 'mapbox-gl/dist/mapbox-gl.css'

export interface LivingGlobeProps {
	profiles: ProfileData[]
	onProfileClick?: (profile: ProfileData) => void
	autoZoomToNewest?: boolean
}

// Marker component for a profile
function ProfileMarker({
	profile,
	onProfileClick,
}: {
	profile: ProfileData
	onProfileClick?: (profile: ProfileData) => void
}) {
	const markerRef = useRef<HTMLDivElement>(null)
	const innerRef = useRef<HTMLDivElement>(null)

	const handleMouseEnter = () => {
		if (markerRef.current) {
			markerRef.current.style.transform = 'scale(1.3)'
		}
		if (innerRef.current) {
			innerRef.current.style.borderColor = '#00f0d0'
			innerRef.current.style.boxShadow = '0 0 20px #00f0d0'
		}
	}

	const handleMouseLeave = () => {
		if (markerRef.current) {
			markerRef.current.style.transform = 'scale(1)'
		}
		if (innerRef.current) {
			innerRef.current.style.borderColor = '#00f0d0'
			innerRef.current.style.boxShadow = '0 0 10px #00f0d0'
		}
	}

	const handleClick = () => {
		// Track marker click
		track(EVENTS.MAP.MARKER_CLICK, {
			x_handle: profile.x_handle,
			city_name: profile.city_name,
		})

		if (onProfileClick) {
			onProfileClick(profile)
		}
	}

	return (
		<div
			ref={markerRef}
			className="mapbox-marker"
			style={{
				cursor: 'pointer',
				transition: 'all 0.3s ease',
			}}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onClick={handleClick}
		>
			<div
				ref={innerRef}
				style={{
					width: '32px',
					height: '32px',
					borderRadius: '50%',
					overflow: 'hidden',
					border: '2px solid #00f0d0',
					boxShadow: '0 0 10px #00f0d0',
					transition: 'all 0.3s ease',
					background: '#00f0d0',
				}}
			>
				<img
					src={
						profile.avatar_url ||
						'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2232%22 height=%2232%22%3E%3Crect width=%2232%22 height=%2232%22 fill=%22%236366f1%22/%3E%3C/svg%3E'
					}
					alt={profile.x_handle}
					style={{
						width: '100%',
						height: '100%',
						objectFit: 'cover',
					}}
					onError={(e) => {
						const target = e.target as HTMLImageElement
						// Fallback to colored rectangle if avatar fails to load
						target.src =
							'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2232%22 height=%2232%22%3E%3Crect width=%2232%22 height=%2232%22 fill=%22%236366f1%22/%3E%3C/svg%3E'
					}}
				/>
			</div>
		</div>
	)
}

export function LivingGlobe({
	profiles,
	onProfileClick,
	autoZoomToNewest = true,
}: LivingGlobeProps) {
	const mapRef = useRef<MapRef>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [bounds, setBounds] = useState<[number, number, number, number]>([
		-180, -90, 180, 90,
	])
	const [zoom, setZoom] = useState(1.5)
	const [selectedCluster, setSelectedCluster] = useState<ProfileData[] | null>(
		null,
	)

	// Get Mapbox access token from global ENV
	const mapboxToken = ENV.MAPBOX_ACCESS_TOKEN

	// Convert profiles to GeoJSON points
	const points = useMemo(
		() =>
			profiles.map((profile) => ({
				type: 'Feature' as const,
				properties: { ...profile, cluster: false },
				geometry: {
					type: 'Point' as const,
					coordinates: [profile.lng, profile.lat] as [number, number],
				},
			})),
		[profiles],
	)

	// Get clusters from supercluster
	// maxZoom: 11 means clustering stops at zoom 12+
	const { clusters, supercluster } = useSupercluster({
		points,
		bounds,
		zoom,
		options: { radius: 75, maxZoom: 11 },
	})

	// At zoom 12+, group individual points that share the same coordinates
	// and prepare them for offset rendering
	const processedClusters = useMemo(() => {
		if (zoom < 12) return clusters

		// Group individual points by coordinates
		type ClusterType = (typeof clusters)[number]
		const coordinateGroups: Record<string, ClusterType[]> = {}
		const result: ClusterType[] = []

		clusters.forEach((cluster) => {
			const isCluster = cluster.properties.cluster === true

			if (isCluster) {
				// Keep regular clusters as-is
				result.push(cluster)
			} else {
				// Group individual points by coordinates
				const [lng, lat] = cluster.geometry.coordinates
				if (lng === undefined || lat === undefined) return

				const key = `${lng.toFixed(6)},${lat.toFixed(6)}`

				if (!coordinateGroups[key]) {
					coordinateGroups[key] = []
				}
				coordinateGroups[key].push(cluster)
			}
		})

		// Process coordinate groups
		Object.values(coordinateGroups).forEach((group: ClusterType[]) => {
			if (group.length === 1 && group[0]) {
				// Single point, add as-is
				result.push(group[0])
			} else if (group.length > 1 && group[0]) {
				// Multiple points at same location - create a pseudo-cluster
				const [lng, lat] = group[0].geometry.coordinates
				if (lng === undefined || lat === undefined) return

				const groupProfiles = group.map((g) => g.properties as ProfileData)

				// Create a synthetic cluster-like feature
				result.push({
					type: 'Feature' as const,
					id: `group-${lng}-${lat}`,
					properties: {
						cluster: true,
						cluster_id: -1, // Synthetic ID
						point_count: group.length,
						point_count_abbreviated: group.length.toString(),
						_isCoordinateGroup: true, // Flag to identify this as a coordinate group
						_profiles: groupProfiles, // Store profiles directly
					} as any,
					geometry: {
						type: 'Point' as const,
						coordinates: [lng, lat],
					},
				} as ClusterType)
			}
		})

		return result
	}, [clusters, zoom])

	// Handle map move to update bounds and zoom
	const handleMove = useCallback(() => {
		if (!mapRef.current) return
		const map = mapRef.current.getMap()
		const mapBounds = map.getBounds()
		if (!mapBounds) return
		setBounds([
			mapBounds.getWest(),
			mapBounds.getSouth(),
			mapBounds.getEast(),
			mapBounds.getNorth(),
		])
		setZoom(map.getZoom())
	}, [])

	// Handle map load
	const handleLoad = useCallback(() => {
		setIsLoading(false)
		handleMove() // Initialize bounds
	}, [handleMove])

	// Handle cluster click - always show modal immediately
	const handleClusterClick = useCallback(
		(
			clusterId: number,
			longitude: number,
			latitude: number,
			isCoordinateGroup: boolean,
			groupProfiles?: ProfileData[],
		) => {
			// If this is a coordinate group (zoom 12+ with same location), show modal
			if (isCoordinateGroup && groupProfiles) {
				setSelectedCluster(groupProfiles)
				return
			}

			// Regular cluster - get all profiles and show modal
			if (!supercluster) return

			const leaves = supercluster.getLeaves(clusterId, Infinity)
			const clusterProfiles = leaves.map((leaf) => leaf.properties)
			setSelectedCluster(clusterProfiles)
		},
		[supercluster],
	)

	// Auto-zoom to newest user
	useEffect(() => {
		if (
			!mapRef.current ||
			!autoZoomToNewest ||
			profiles.length === 0 ||
			isLoading
		)
			return

		// Get the newest profile
		const newest = profiles.reduce((prev, current) => {
			return new Date(current.created_at) > new Date(prev.created_at)
				? current
				: prev
		})

		// Zoom to the newest user's location
		setTimeout(() => {
			mapRef.current?.flyTo({
				center: [newest.lng, newest.lat],
				zoom: 8,
				duration: 2000, // 2 second transition
			})
		}, 500)
	}, [profiles, autoZoomToNewest, isLoading])

	if (!mapboxToken) {
		return (
			<div className="flex h-full w-full items-center justify-center">
				<div className="text-center">
					<p className="text-text-muted">
						Mapbox access token is not configured
					</p>
				</div>
			</div>
		)
	}

	return (
		<div style={{ position: 'relative', width: '100%', height: '100%' }}>
			{isLoading && (
				<div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50">
					<div className="text-center">
						<div className="border-primary mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2"></div>
						<p className="text-text-muted">Loading map...</p>
					</div>
				</div>
			)}
			<Map
				ref={mapRef}
				mapboxAccessToken={mapboxToken}
				initialViewState={{
					longitude: 0,
					latitude: 20,
					zoom: 1.5,
				}}
				style={{ width: '100%', height: '100%' }}
				mapStyle="mapbox://styles/gianlucas90/cmi4cjje4005i01qq2vkj724s"
				projection="globe"
				onLoad={handleLoad}
				onMove={handleMove}
			>
				{processedClusters.map((cluster) => {
					const [longitude, latitude] = cluster.geometry.coordinates
					const isCluster = cluster.properties.cluster === true

					if (isCluster) {
						const clusterProps = cluster.properties as {
							cluster: true
							cluster_id: number
							point_count: number
							_isCoordinateGroup?: boolean
							_profiles?: ProfileData[]
						}

						// Check if this is a coordinate group (zoom 12+ same location)
						const isCoordinateGroup = clusterProps._isCoordinateGroup === true
						let clusterProfiles: ProfileData[] = []

						if (isCoordinateGroup) {
							// Use pre-stored profiles for coordinate groups
							clusterProfiles = clusterProps._profiles || []
						} else {
							// Get profiles from supercluster for regular clusters
							const clusterLeaves = supercluster?.getLeaves(
								clusterProps.cluster_id,
								Infinity,
							)
							clusterProfiles =
								clusterLeaves?.map((leaf) => leaf.properties) || []
						}

						return (
							<Marker
								key={`cluster-${cluster.id}`}
								longitude={longitude ?? 0}
								latitude={latitude ?? 0}
								anchor="center"
							>
								<ClusterMarker
									profiles={clusterProfiles}
									pointCount={clusterProps.point_count}
									onClusterClick={() =>
										handleClusterClick(
											clusterProps.cluster_id,
											longitude ?? 0,
											latitude ?? 0,
											isCoordinateGroup,
											clusterProfiles,
										)
									}
								/>
							</Marker>
						)
					}

					// Individual point
					const profile = cluster.properties as ProfileData
					return (
						<Marker
							key={profile.x_handle}
							longitude={longitude ?? 0}
							latitude={latitude ?? 0}
							anchor="center"
						>
							<ProfileMarker
								profile={profile}
								onProfileClick={onProfileClick}
							/>
						</Marker>
					)
				})}
			</Map>

			{/* Profile cluster modal */}
			<ProfileClusterModal
				profiles={selectedCluster || []}
				isOpen={selectedCluster !== null}
				onClose={() => setSelectedCluster(null)}
				onProfileClick={(profile) => {
					// Keep modal open when profile is clicked
					onProfileClick?.(profile)
				}}
			/>
		</div>
	)
}
